/**
 * Client-side "page not found" fallback for the icarus SPA.
 *
 * `@apexl/icarus-core/src/router/index.js` ships a single catch-all route
 * (`/:catchAll(.*)`) and there is no `<router-view>` anywhere in this app —
 * `Icarus.vue` is mounted directly as the app root (`createApp(Icarus)` in
 * `index.js`) and reads `route.path`/`route.query` itself to ask the
 * BACKEND "what page lives here" via `usePageGetter().getPage(route.path)`
 * -> `GET {route.path}` (see every `routes/dashboard/*.php` file's docblock
 * for this exact wording — it's the load-bearing convention comment across
 * every module that registers a dashboard page route). Vue Router therefore
 * does no view-switching of its own in this app; it only exists so
 * `Icarus.vue` can read the current path/query reactively.
 *
 * That means visiting an unregistered page path (e.g. `/alerts` — a
 * plausible guess, since the nav labels the section "Alerts", when the real
 * route is `/servers/alerts`) makes the page-fetch 404. `usePageGetter`'s
 * catch swallows anything that isn't a 403 silently — no console error, no
 * redirect — leaving `pageComponents` at its initial empty array. Nothing
 * renders, and the static shell's "IO SaaS Framework Loading…" title never
 * clears, which is indistinguishable from a crashed app.
 *
 * `usePageGetter`/`Icarus.vue` are vendor (`@apexl/icarus-core`) — per this
 * project's CLAUDE.md, a vendor behaviour change belongs in a
 * composer-patches/patch-package patch upstreamed to the package, not a
 * direct edit here, and forking either would mean re-implementing the whole
 * page-fetch/render cycle. The same "least invasive extension point the
 * framework actually offers" `installerGuard.js` already documents — a
 * `beforeEach` registered on the exported router instance, before
 * `app.use(router)` installs it in `index.js` — is used here instead: catch
 * the bad navigation BEFORE Icarus.vue ever asks the backend for it, and
 * redirect to `/not-found`, a route that IS registered server-side
 * (`app\module\shell\Controller\NotFoundDisplayController`) and so resolves
 * through the exact same page-fetch cycle as every other page.
 *
 * `KNOWN_ROUTE_PATTERNS` is a static allow-list, not a live probe of the
 * backend on every navigation — issuing a second network request on every
 * single page load just to ask "does this exist" would double the cost of
 * every legitimate navigation for the sake of the rare bad-link case, and
 * the set of real dashboard routes only changes when a module adds one.
 * Mirrors every `routeManager->get('*.dashboard.*', '/path', ...)`
 * registration in the app (under each module's `routes/dashboard/` dir) plus
 * the handful `apexl/io-display`/`apexl/io-user` ship (`/`, `/login*`,
 * `/register`, `/forgot-password*`, `/reset-password*`). Add a pattern here
 * whenever a new dashboard page route is registered server-side — this list
 * is intentionally the one place that knowledge is duplicated client-side,
 * not spread across ad-hoc checks.
 *
 * ⚠ Do not write a glob path containing a star-slash in this docblock (the
 * v0.9.3 version said `api/module/<star>/routes/dashboard/`). That sequence
 * CLOSES the block comment: the rest of the line became live code, `routes`
 * resolved to nothing, and the module threw `ReferenceError: routes is not
 * defined` at import time — taking the whole SPA down with a blank page and
 * a clean webpack build. Spell directory shapes out in prose instead.
 */
const KNOWN_ROUTE_PATTERNS = [
  /^\/$/, // home (apexl/io-display)
  /^\/login(\/[^/]+)?$/, // /login, /login/nfc, /login/{loginMessageType}
  /^\/register$/,
  /^\/forgot-password(\/check-email)?$/,
  /^\/reset-password\/[^/]+$/,
  /^\/reset-password-success$/,
  /^\/install$/,
  /^\/not-found$/,
  /^\/servers$/,
  /^\/servers\/alerts$/,
  /^\/servers\/pressure$/,
  /^\/servers\/[^/]+$/, // /servers/{id}
  /^\/security$/,
  /^\/inventory$/,
  /^\/logs$/,
  /^\/domains$/,
  /^\/integrations$/,
  /^\/projects\/provisioning-runs$/,
  /^\/projects\/provisioning-runs\/[^/]+$/, // /projects/provisioning-runs/{id}
];

/** Exported standalone so it's unit-testable without a router instance. */
export function isKnownRoute(path) {
  return KNOWN_ROUTE_PATTERNS.some((pattern) => pattern.test(path));
}

function installNotFoundGuard(router) {
  router.beforeEach((to) => {
    if (isKnownRoute(to.path)) {
      return true;
    }

    return { path: '/not-found', query: { from: to.fullPath } };
  });
}

export default installNotFoundGuard;
